import {
  INCLUDE_CLIENTS,
  INCLUDE_DEVICE_MANAGEMENT,
  INCLUDE_EXERCISE_PROGRAMS,
  INCLUDE_EXERCISES,
  INCLUDE_LIVE_DASHBOARD,
  INCLUDE_LIVE_METRICS,
  INCLUDE_MAP,
  INCLUDE_REPORTS,
  INCLUDE_SHIFT_AND_ROUND_REPORTS,
  PATIENT_NOUN,
} from '@/constants/app-variables'

export type SideNavigationType = {
  title: string
  additionalCssClass: string
  menus: SideNavigationMenuType[]
}

export type SideNavigationMenuType = {
  name: string
  to?: { name: string }
  exact?: boolean
  children?: SideNavigationMenuType[]
}

export const COMMON_ROUTES: { [key: string]: SideNavigationMenuType } = {
  DASHBOARD: { name: 'Dashboard', to: { name: 'home' }, exact: true },

  //Exercise Programs
  EXERCISE_PROGRAMS: { name: 'My Programs', to: { name: 'exercise-programs' } },

  LIVE_DASHBOARD: { name: 'Live Dashboard', to: { name: 'live-dashboard' } },
  PATIENTS: { name: `My ${PATIENT_NOUN}s`, to: { name: 'patients' } },
  MAP: { name: PATIENT_NOUN + ' Map', to: { name: 'map' } },
  SHIFT_AND_ROUND_REPORTS: {
    name: 'Shift & Round Reports',
    to: { name: 'shift-and-round-reports' },
  },
  REPORTS: {
    name: 'Reports',
    children: [
      INCLUDE_EXERCISES ? { name: 'Exercises', to: { name: 'exercises' } } : null,
      { name: PATIENT_NOUN + ' Summaries', to: { name: 'patients-summaries' } },
      INCLUDE_LIVE_METRICS ? { name: 'All Metrics Live', to: { name: 'all-metrics-live' } } : null,
    ].filter(x => !!x), // remove null values
  },

  SITE_STATS: {
    name: 'Site Stats',
    to: { name: 'site-stats' },
  },

  //Device Management
  DEVICE_MANAGEMENT: {
    name: 'Device Management',
    to: { name: 'device-management' },
  },
  ADMIN_SITE_MANAGEMENT: {
    name: 'Admin Site Management',
    to: { name: 'admin-site-management' },
  },
  ORG_SITE_MANAGEMENT: {
    name: 'Site Management',
    to: { name: 'org-site-management' },
  },
}

export const NURSING_HOME_ROUTES: { [key: string]: SideNavigationMenuType } = {
  PATIENTS: {
    name: `${PATIENT_NOUN}s`,
    children: [
      { name: 'Map View', to: { name: 'map' } },
      { name: 'List View', to: { name: 'patients' } },
    ],
  },
  SHIFT_AND_ROUND_REPORTS: {
    name: 'Reports',
    to: { name: 'shift-and-round-reports' },
  },

  SITE_STATS: {
    name: 'Site Stats',
    to: { name: 'site-stats' },
  },
  //Device Management
  DEVICE_MANAGEMENT: {
    name: 'Device Management',
    to: { name: 'device-management' },
  },
  ADMIN_SITE_MANAGEMENT: {
    name: 'Admin Site Management',
    to: { name: 'admin-site-management' },
  },
}

function buildMyClientsMenuFromSiteStatses(siteStatses): SideNavigationMenuType[] {
  let clients = siteStatses
    .map(siteStats => ({
      name: siteStats.siteName || '',
      to: { name: 'site-stats-detail', params: { site_stats_id: siteStats.id } },
    }))
    .sort((a, b) => a.name.localeCompare(b.name))

  const names = clients.map(o => o.name)
  return clients.filter(({ name }, index) => !names.includes(name, index + 1))
}

export function getCareTeamNavigations(): [SideNavigationType] {
  return [
    {
      title: 'MY PRACTICE',
      additionalCssClass: 'u-display-1',
      menus: [
        COMMON_ROUTES.DASHBOARD,
        INCLUDE_LIVE_DASHBOARD ? COMMON_ROUTES.LIVE_DASHBOARD : null,
        INCLUDE_EXERCISE_PROGRAMS ? COMMON_ROUTES.EXERCISE_PROGRAMS : null,
        COMMON_ROUTES.PATIENTS,
        INCLUDE_MAP ? COMMON_ROUTES.MAP : null,
        INCLUDE_SHIFT_AND_ROUND_REPORTS ? COMMON_ROUTES.SHIFT_AND_ROUND_REPORTS : null,
        INCLUDE_REPORTS ? COMMON_ROUTES.REPORTS : null,
        INCLUDE_DEVICE_MANAGEMENT ? COMMON_ROUTES.DEVICE_MANAGEMENT : null,
      ].filter(x => !!x),
    },
  ]
}

export function getNursingHomeNavigations(siteStatses: any[], includeAdmin: boolean): SideNavigationType[] {
  return [
    {
      title: 'MY RESIDENCE',
      additionalCssClass: 'u-display-1',
      menus: [
        NURSING_HOME_ROUTES.PATIENTS,
        INCLUDE_SHIFT_AND_ROUND_REPORTS ? NURSING_HOME_ROUTES.SHIFT_AND_ROUND_REPORTS : null,
        // COMMON_ROUTES.ORG_SITE_MANAGEMENT,
        includeAdmin ? COMMON_ROUTES.SITE_STATS : null,
        includeAdmin ? COMMON_ROUTES.ADMIN_SITE_MANAGEMENT : null,
      ].filter(x => !!x),
    },
    {
      title: 'MY CARE HOMES',
      additionalCssClass: 'u-label-2 black--text',
      menus: buildMyClientsMenuFromSiteStatses(siteStatses),
    },
  ]
}

export function getExecNavigations(siteStatses: any[]): SideNavigationType[] {
  return [
    {
      title: 'MY PRACTICE',
      additionalCssClass: 'u-display-1',
      menus: [COMMON_ROUTES.DASHBOARD],
    },
    {
      title: 'MY CLIENTS',
      additionalCssClass: 'u-label-2 black--text',
      menus: buildMyClientsMenuFromSiteStatses(siteStatses),
    },
  ]
}

export function getAdminNavigations(siteStatses: any[]): SideNavigationType[] {
  return [
    {
      title: 'MY PRACTICE',
      additionalCssClass: 'u-display-1',
      menus: [
        COMMON_ROUTES.DASHBOARD,
        INCLUDE_EXERCISE_PROGRAMS ? COMMON_ROUTES.EXERCISE_PROGRAMS : null,
        INCLUDE_LIVE_DASHBOARD ? COMMON_ROUTES.LIVE_DASHBOARD : null,
        COMMON_ROUTES.PATIENTS,
        INCLUDE_MAP ? COMMON_ROUTES.MAP : null,
        INCLUDE_SHIFT_AND_ROUND_REPORTS ? COMMON_ROUTES.SHIFT_AND_ROUND_REPORTS : null,
        INCLUDE_REPORTS ? COMMON_ROUTES.REPORTS : null,
        INCLUDE_DEVICE_MANAGEMENT ? COMMON_ROUTES.DEVICE_MANAGEMENT : null,
        COMMON_ROUTES.SITE_STATS,
        COMMON_ROUTES.ADMIN_SITE_MANAGEMENT,
        COMMON_ROUTES.ORG_SITE_MANAGEMENT,
      ].filter(x => !!x),
    },
    INCLUDE_CLIENTS
      ? {
          title: 'MY CLIENTS',
          additionalCssClass: 'u-label-2 black--text',
          menus: buildMyClientsMenuFromSiteStatses(siteStatses),
        }
      : null,
  ].filter(x => !!x)
}

export function getRocheConsultantNavigations(siteStatses: any[]): SideNavigationType[] {
  return [
    {
      title: 'ALL HOMES',
      additionalCssClass: 'u-display-1',
      menus: [
        // COMMON_ROUTES.DASHBOARD,
        // INCLUDE_EXERCISE_PROGRAMS ? COMMON_ROUTES.EXERCISE_PROGRAMS : null,
        // INCLUDE_LIVE_DASHBOARD ? COMMON_ROUTES.LIVE_DASHBOARD : null,
        // COMMON_ROUTES.PATIENTS,
        // INCLUDE_MAP ? COMMON_ROUTES.MAP : null,
        // INCLUDE_SHIFT_AND_ROUND_REPORTS ? COMMON_ROUTES.SHIFT_AND_ROUND_REPORTS : null,
        // INCLUDE_REPORTS ? COMMON_ROUTES.REPORTS : null,
        // INCLUDE_DEVICE_MANAGEMENT ? COMMON_ROUTES.DEVICE_MANAGEMENT : null,
        COMMON_ROUTES.SITE_STATS,
        // COMMON_ROUTES.ADMIN_SITE_MANAGEMENT,
        // COMMON_ROUTES.ORG_SITE_MANAGEMENT,
      ].filter(x => !!x),
    },
    // {
    //   title: 'MY CLIENTS',
    //   additionalCssClass: 'u-label-2 black--text',
    //   menus: buildMyClientsMenuFromSiteStatses(siteStatses),
    // },
  ]
}
